// src/components/professor/Documentation.js

import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import Navbar from '../../components/professor/NavBar';
import SidebarDocumentation from '../../components/professor/SidebarDocumentation';
import Preloader from '../../components/Preloader';

function Documentation() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const sectionRefs = useRef([]);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        window.location.href = '/sign-in';
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      threshold: 0.6,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    sectionRefs.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      if (observer) {
        sectionRefs.current.forEach((section) => {
          if (section) observer.unobserve(section);
        });
      }
    };
  }, [searchQuery]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.href = '/sign-in';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const documentationSections = [
    {
      id: 'installation-section',
      title: 'Installation',
      text: `Installation
To get started with our application, follow these steps:
Clone the repository: git clone https://github.com/your-repo-url.git
Install dependencies: npm install
Configure Firebase: Set up your Firebase project and replace the configuration in firebase.js with your own.
Start the application: npm start
For a detailed guide, watch the installation video below:`,
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-2">Installation</h2>
          <p className="text-base">
            To get started with our application, follow these steps:
          </p>
          <ol className="list-decimal list-inside ml-6 space-y-2">
            <li>
              <strong>Clone the repository:</strong>
              <pre className="bg-[var(--card-bg)] p-4 rounded text-[var(--text-color)] overflow-auto">
                <code>git clone https://github.com/your-repo-url.git</code>
              </pre>
            </li>
            <li>
              <strong>Install dependencies:</strong>
              <pre className="bg-[var(--card-bg)] p-4 rounded text-[var(--text-color)] overflow-auto">
                <code>npm install</code>
              </pre>
            </li>
            <li>
              <strong>Configure Firebase:</strong>
              <p>
                Set up your Firebase project and replace the configuration in{' '}
                <code>firebase.js</code> with your own.
              </p>
            </li>
            <li>
              <strong>Start the application:</strong>
              <pre className="bg-[var(--card-bg)] p-4 rounded text-[var(--text-color)] overflow-auto">
                <code>npm start</code>
              </pre>
            </li>
          </ol>
          <p>For a detailed guide, watch the installation video below:</p>
          <div className="aspect-video w-full">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/your-video-id"
              allowFullScreen
              title="Installation Video"
            ></iframe>
          </div>
        </div>
      ),
    },
    {
      id: 'setup-section',
      title: 'Setup',
      text: `Setup
After installing, set up the application as follows:
Create a Firebase project: Go to the Firebase console and create a new project.
Enable Authentication: In your Firebase project, enable Email/Password authentication.
Set up Firestore and Realtime Database: Create the necessary collections and nodes as per the database structure below.
Database Structure
Firestore users collection
users => uid => {
 first_name: string,
 last_name: string,
 role: string (professor/student),
 email: string
}

Firestore classes collection
classes => class_id => {
 professor_uid: string,
 class_name: string,
 class_invitation: string,
 start_date: date,
 end_date: date,
 statsaccess: true/false,
 gradeaccess: true,
 sessionaccess: true,
}

Firestore enrollments collection
enrollments => class_id => {
 Array of ( student_uid: string )
}

Firestore grades collection
grades => class_id => {
 student_uid => {
 session_id: string,
 points: Array of {
 question_number: int,
 point: int
 }
 }
}`,
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-2">Setup</h2>
          <p>After installing, set up the application as follows:</p>
          <ol className="list-decimal list-inside ml-6 space-y-2">
            <li>
              <strong>Create a Firebase project:</strong>
              <p>Go to the Firebase console and create a new project.</p>
            </li>
            <li>
              <strong>Enable Authentication:</strong>
              <p>In your Firebase project, enable Email/Password authentication.</p>
            </li>
            <li>
              <strong>Set up Firestore and Realtime Database:</strong>
              <p>
                Create the necessary collections and nodes as per the database
                structure below.
              </p>
              <h3 className="text-xl font-semibold mb-2">Database Structure</h3>
              <img
                src="path/to/database-structure-image.png"
                alt="Database Structure"
                className="w-full h-auto mb-3"
              />
              <pre className="bg-[var(--card-bg)] p-4 rounded text-[var(--text-color)] overflow-auto">
                <code>{`// Firestore users collection
users => uid => {
  first_name: string,
  last_name: string,
  role: string (professor/student),
  email: string
}

// Firestore classes collection
classes => class_id => {
  professor_uid: string,
  class_name: string,
  class_invitation: string,
  start_date: date,
  end_date: date,
  statsaccess: true/false,
  gradeaccess: true,
  sessionaccess: true,
}

// Firestore enrollments collection
enrollments => class_id => {
  Array of ( student_uid: string )
}

// Firestore grades collection
grades => class_id => {
  student_uid => {
    session_id: string,
    points: Array of {
      question_number: int,
      point: int
    }
  }
}
`}</code>
              </pre>
            </li>
          </ol>
        </div>
      ),
    },
    {
      id: 'troubleshooting-section',
      title: 'Troubleshooting',
      text: `Troubleshooting
If you encounter issues, try the following steps:
Ensure all Firebase services are correctly set up.
Check your internet connection.
Refer to the error logs in the console for specific errors.
For more help, watch the troubleshooting video:`,
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-2">Troubleshooting</h2>
          <p>If you encounter issues, try the following steps:</p>
          <ul className="list-disc list-inside ml-6 space-y-1">
            <li>Ensure all Firebase services are correctly set up.</li>
            <li>Check your internet connection.</li>
            <li>Refer to the error logs in the console for specific errors.</li>
          </ul>
          <p>For more help, watch the troubleshooting video:</p>
          <div className="aspect-video w-full">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/your-video-id"
              allowFullScreen
              title="Troubleshooting Video"
            ></iframe>
          </div>
        </div>
      ),
    },
    {
      id: 'exports-section',
      title: 'Data Exports',
      text: `Data Exports
Professors can export data for analysis:
Export student grades.
Export session statistics.
Use the export feature in the dashboard to download CSV files.`,
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-2">Data Exports</h2>
          <p>Professors can export data for analysis:</p>
          <ul className="list-disc list-inside ml-6 space-y-1">
            <li>Export student grades.</li>
            <li>Export session statistics.</li>
          </ul>
          <p>Use the export feature in the dashboard to download CSV files.</p>
        </div>
      ),
    },
    {
      id: 'tips-section',
      title: 'Tips and Tricks',
      text: `Tips and Tricks
Enhance your experience with these tips:
Use the session timer to manage class time efficiently.
Encourage student participation by using interactive questions.
Regularly backup your data.`,
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-2">Tips and Tricks</h2>
          <p>Enhance your experience with these tips:</p>
          <ul className="list-disc list-inside ml-6 space-y-1">
            <li>Use the session timer to manage class time efficiently.</li>
            <li>
              Encourage student participation by using interactive questions.
            </li>
            <li>Regularly backup your data.</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'signup-signin-section',
      title: 'Sign Up and Sign In',
      text: `Sign Up and Sign In
Users can sign up and sign in as follows:
Sign Up
New users can create an account by providing:
First Name
Last Name
Email
Password
Role (Professor/Student)
Sign In
Existing users can sign in using their email and password.`,
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-2">Sign Up and Sign In</h2>
          <p>Users can sign up and sign in as follows:</p>
          <h3 className="text-xl font-semibold mb-2">Sign Up</h3>
          <p>New users can create an account by providing:</p>
          <ul className="list-disc list-inside ml-6 space-y-1">
            <li>First Name</li>
            <li>Last Name</li>
            <li>Email</li>
            <li>Password</li>
            <li>Role (Professor/Student)</li>
          </ul>
          <h3 className="text-xl font-semibold mb-2">Sign In</h3>
          <p>Existing users can sign in using their email and password.</p>
          <img
            src="path/to/signup-signin-image.png"
            alt="Sign Up and Sign In"
            className="w-full h-auto"
          />
        </div>
      ),
    },
  ];

  const filteredSections = documentationSections.filter(
    (section) =>
      section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <Preloader />;
  }

  return (
    <div className="bg-[var(--bg-color)] text-[var(--text-color)] min-h-screen transition-all">
    
      {/* Main Container */}
      <div className="max-w-screen-xl mx-auto pt-20 px-4 flex flex-col md:flex-row gap-8">
        {/* Sidebar */}
        <div className="md:w-1/4">
          <SidebarDocumentation activeSection={activeSection} />
        </div>

        {/* Main Content */}
        <main className="md:w-3/4">
          {/* Search Input */}
          <input
            type="text"
            className="mb-4 p-2 w-full max-w-md border border-[var(--border-color)] rounded-md
                       bg-[var(--input-bg)] text-[var(--text-color)]
                       focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                       focus:border-transparent transition-all"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* Documentation Sections */}
          {filteredSections.length > 0 ? (
            filteredSections.map((section, index) => (
              <div
                key={section.id}
                id={section.id}
                className="mb-8"
                ref={(el) => (sectionRefs.current[index] = el)}
              >
                {section.content}
              </div>
            ))
          ) : (
            <p className="text-base">No documentation sections match your search.</p>
          )}
        </main>
      </div>
    </div>
  );
}

export default Documentation;
